import moment from 'moment';
import fr from 'facet-rest';

import SkipSentryError from 'Errors/SkipSentryError';

const initialNotificationGroupState = {
  totalCount: 0,
  unreadNotificationCount: 0,
  notifications: {},
  meta: null,
};

function groupNotifications(notifications) {
  return notifications.reduce(
    (root, n) => {
      const groupKey = n.attributes.group + n.attributes.type;
      const hasGroup = Boolean(root.notifications[groupKey]);
      let { unreadNotificationCount } = root;

      if (!n.attributes.viewedOn) {
        unreadNotificationCount += 1;
      }

      if (hasGroup) {
        const group = { ...root.notifications[groupKey] };

        if (!n.attributes.viewedOn) {
          group.viewedOn = null;
        }

        return {
          ...root,
          unreadNotificationCount,
          notifications: {
            ...root.notifications,
            [groupKey]: {
              ...group,
              count: group.count + 1,
              related: [
                ...group.related,
                {
                  id: n.id,
                  viewedOn: n.attributes.viewedOn,
                  group: groupKey,
                  createdBy: n.attributes.createdBy,
                  createdAt: n.attributes.createdAt,
                  householdId: n.attributes.householdId,
                  primaryClientFullName: n.attributes.primaryClientFullName,
                  fileId: n.attributes.fileId,
                  actionItemId: n.attributes.actionItemId,
                  links: n.links,
                  description: n.attributes.description,
                  relationships: n.relationships,
                  type: n.attributes.type,
                  calendarEventId: n.attributes.calendarEventId,
                  meta: n.meta,
                },
              ],
            },
          },
        };
      }

      return {
        ...root,
        unreadNotificationCount,
        notifications: {
          ...root.notifications,
          [groupKey]: {
            id: n.id,
            count: 1,
            viewedOn: n.attributes.viewedOn,
            group: groupKey,
            createdBy: n.attributes.createdBy,
            createdAt: n.attributes.createdAt,
            primaryClientFullName: n.attributes.primaryClientFullName,
            householdId: n.attributes.householdId,
            fileId: n.attributes.fileId,
            actionItemId: n.attributes.actionItemId,
            links: n.links,
            description: n.attributes.description,
            related: [],
            relationships: n.relationships,
            type: n.attributes.type,
            calendarEventId: n.attributes.calendarEventId,
            meta: n.meta,
          },
        },
        meta: n.meta, // hoist meta to the root
      };
    },
    { ...initialNotificationGroupState, totalCount: notifications.length },
  );
}

export default function useNotificationState(api) {
  const fetchNotificationsAndGroup = (filters = {}, url = null) => {
    const {
      primaryClientFullName,
      status,
      type,
      createdAtMin,
      createdAtMax,
    } = filters;
    const queries = {
      status: status !== '' && status ? `eq|${status}` : undefined,
      primaryClientFullName: primaryClientFullName
        ? `like|${primaryClientFullName}`
        : undefined,
      createdAtMin: createdAtMin ? `gt|${createdAtMin}` : undefined,
      createdAtMax: createdAtMax ? `lt|${createdAtMax}` : undefined,
    };

    if (Array.isArray(type) && type.length > 0) {
      queries.type = type.join(',');
    } else if (type && type !== '') {
      queries.type = type;
    }

    if (url !== null && new window.URL(url)) {
      return fr.http.Get(url).then((response) => {
        if (response.status === 200 && Array.isArray(response.data)) {
          return groupNotifications(response.data);
        }
        return initialNotificationGroupState;
      });
    }

    return api
      .getNotifications({
        ...filters,
        ...queries,
      })
      .catch((err) => {
        if (err.message === 'Unauthorized' || err.message === "Whoops! Let's try again.") {
          throw new SkipSentryError('Unauthorized GET: Notifications');
        }

        // rethrow otherwise
        throw err;
      })
      .then((response) => {
        if (response.statusCode === 200 && Array.isArray(response.data)) {
          return groupNotifications(response.data);
        }
        return initialNotificationGroupState;
      });
  };

  const markNotificationsRead = filters => api.markNotificationsRead(filters).then((response) => {
    if (response.statusCode === 200 && Array.isArray(response.data)) {
      return groupNotifications(response.data);
    }
    return initialNotificationGroupState;
  });

  const markNotificationRead = id => api
    .updateNotification(id, { attributes: { viewedOn: moment().format() } })
    .then((response) => {
      if (response.statusCode === 200 && Array.isArray(response.data)) {
        return groupNotifications(response.data);
      }
      return initialNotificationGroupState;
    });

  return {
    fetchNotifications: fetchNotificationsAndGroup,
    markNotificationRead,
    markNotificationsRead,
  };
}
